import { EditIcon } from '@chakra-ui/icons'
import { Flex, IconButton, Text } from '@chakra-ui/react'
import { ProfilePicturePopover } from '@components/Admins/ProfilePicturePopover'
import { Assessment, getCorrectedValue, getDateString, getGestationalAgeDays } from '@hb/shared'
import { getGravidaPara } from '@utils/assessments/gravidaPara'
import { useMemo, useState } from 'react'
import { useProfile } from '../../../../../../contexts/user/ProfileContext'
import { useUpdateDoc } from '../../../../../../hooks/backend/useUpdateDoc'
import { NameEditModal } from '../../../Patient/NameEditModal'
import { NicknameView } from '../../../Patient/NicknameView'
import { AlternatePronounsView } from '../../../Patient/PronounsView'
import { PatientInfoContact } from './PatientContact'

const getAgeFromDob = (dob: number) => {
  const now = new Date()
  const birth = new Date(dob)
  let age = now.getUTCFullYear() - birth.getUTCFullYear()
  const monthDiff = now.getUTCMonth() - birth.getUTCMonth()
  if (monthDiff < 0 || (monthDiff === 0 && now.getUTCDate() < birth.getUTCDate())) age -= 1
  return age
}

const formatGaWeeks = (totalDays: number) => {
  const weeks = Math.floor(totalDays / 7)
  const days = totalDays % 7
  return `${weeks}w ${days}d`
}

// The pregnancy shown in the header: the selected one, falling back to the most recent
const useProfilePregnancy = () => {
  const { selectedAssessment, assessmentId, mostRecentPregnancy } = useProfile()
  return useMemo(() => {
    const populated = selectedAssessment?.populated as Assessment | null
    if (populated) return { pregnancy: populated, pregnancyId: assessmentId }
    if (mostRecentPregnancy)
      return { pregnancy: mostRecentPregnancy as Assessment, pregnancyId: mostRecentPregnancy.id }
    return { pregnancy: null, pregnancyId: null }
  }, [selectedAssessment, assessmentId, mostRecentPregnancy])
}

const DOBView = ({ dob }: { dob: number | null | undefined }) => {
  const age = useMemo(() => (dob ? getAgeFromDob(dob) : null), [dob])
  return (
    <Flex gap={2} align="center">
      <Text lineHeight={1} fontSize="sm" fontWeight={500} color="gray.600">
        DOB:
      </Text>
      <Text fontFamily="Hero-New" fontSize="sm" lineHeight={1} opacity={0.8} fontWeight={500}>
        {dob ? getDateString(dob, 'short') : 'None'}
      </Text>
      {age !== null ? (
        <Text fontFamily="Hero-New" fontSize="sm" lineHeight={1} opacity={0.8} fontWeight={500}>
          ({age} years)
        </Text>
      ) : null}
    </Flex>
  )
}

const DueDateView = () => {
  const { pregnancy } = useProfilePregnancy()
  const edd = useMemo(
    () => getCorrectedValue(pregnancy, 'delivery.edd') as number | undefined,
    [pregnancy],
  )
  const gestationalAgeDays = useMemo(
    () => getGestationalAgeDays(edd, new Date().toISOString()),
    [edd],
  )
  // const deliveredOn = pregnancy?.delivery?.deliveredOn
  // const delivered = !!deliveredOn && deliveredOn !== Infinity
  // if (!edd || delivered) return null
  return (
    <Flex gap={2} align="center">
      <Text lineHeight={1} fontSize="sm" fontWeight={500} color="gray.600">
        Due:
      </Text>
      <Text
        fontFamily="Hero-New"
        fontSize="sm"
        lineHeight={1}
        opacity={edd ? 0.8 : 0.5}
        fontWeight={500}>
        {edd ? getDateString(edd, 'short') : 'Not set'}
      </Text>
      {gestationalAgeDays != null ? (
        <Text fontFamily="Hero-New" lineHeight={1} opacity={0.8} fontWeight={500}>
          ({formatGaWeeks(gestationalAgeDays)})
        </Text>
      ) : null}
    </Flex>
  )
}

const GravidaParaView = () => {
  const { user, assessments } = useProfile()
  const { pregnancy, pregnancyId } = useProfilePregnancy()
  const gravidaPara = useMemo(() => {
    if (!pregnancy) return null
    return getGravidaPara(
      assessments,
      pregnancyId,
      user?.gyn?.mergedData?.gynAndSexualHistory?.gynHistory?.additionalPregnancies ?? [],
    )
  }, [pregnancy, pregnancyId, assessments, user])
  if (!gravidaPara) return null
  return (
    <Flex gap={2} align="center">
      <Text fontFamily="Hero-New" fontSize="sm" lineHeight={1} opacity={0.8} fontWeight={500}>
        G{gravidaPara.gravida}P{gravidaPara.para}
      </Text>
    </Flex>
  )
}

export const PatientName = () => {
  const { user, patientRef, userId } = useProfile()
  const { fname, lname, middleName, dob } = user ?? {}
  const name = useMemo(
    () =>
      `${fname || '[Missing first name]'}${middleName ? ` ${middleName}` : ''} ${lname || '[Missing Last Name]'}`,
    [fname, lname, middleName],
  )

  const update = useUpdateDoc()
  const [nameModalOpen, setNameModalOpen] = useState(false)
  return (
    <Flex
      borderRadius={4}
      // border="1px solid #cdcdcd"
      // bg="whiteAlpha.500"
      gap={1}
      w="100%"
      position="relative"
      px={2}
      py={2}
      flexFlow="column">
      <Flex gap={2} w="100%">
        <ProfilePicturePopover gutter={-50} userId={userId} canEdit size={12} noOnlineIndicator />
        <Flex flex={1} minW={0} gap={1} pb={1} px={0.5} flexFlow="column">
          <Text
            fontSize="lg"
            fontFamily="Hero-New"
            lineHeight={1}
            fontWeight={500}
            color="gray.700">
            {name || 'No name entered'}
          </Text>
          <AlternatePronounsView />
          <NicknameView />
          <DOBView dob={dob} />
          <DueDateView />
          <GravidaParaView />
        </Flex>
      </Flex>
      <IconButton
        position="absolute"
        borderRadius="full"
        w={7}
        h={7}
        right={1}
        top={1}
        variant="ghost"
        _hover={{ bg: 'blackAlpha.100' }}
        ml="auto"
        size="sm"
        aria-label="Edit"
        icon={<EditIcon />}
        onClick={() => setNameModalOpen(true)}
      />
      <PatientInfoContact />
      <NameEditModal
        userType="patient"
        value={user}
        isOpen={nameModalOpen}
        onClose={() => setNameModalOpen(false)}
        onSubmit={v => update(patientRef, '', v)}
      />
    </Flex>
  )
}
